// src/components/compliance/KycProgress.tsx
/**
 * Visible check run (STORY-991 — API immotron-api#873).
 *
 * Shows every single step of a GwG check run as a vertical list with live
 * status, so the customer sees what we compare with instead of a spinner.
 * The data is real — order, status and placeholders come from the API, the
 * component only maps `code`/`status` onto i18n texts.
 *
 * The backend is usually faster than the eye, so finished steps are revealed
 * one at a time with a minimum dwell time (`KYC_STEP_MIN_VISIBLE_MS`).
 */
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BadgeCheck,
  Building2,
  Check,
  Circle,
  Clock,
  Fingerprint,
  Landmark,
  Loader2,
  Minus,
  PenLine,
  ShieldCheck,
} from 'lucide-react'
import usePrefersReducedMotion from '@/hooks/usePrefersReducedMotion'
import {
  KYC_RUN_STATUS,
  KYC_STEP_CODE,
  KYC_STEP_MIN_VISIBLE_MS,
  KYC_STEP_STATUS,
} from '@/constants/kyc'
import { SIGNATURE_TYPE_LABEL_KEYS, SIGNATURE_TYPES } from '@/constants/signature'
import type { KycCheckRun, KycCheckStep, KycStepCodeValue } from '@/types/kyc'

/**
 * How a step is presented. `waiting` is the queued state before the reveal
 * reaches it, `pending` a finished step whose result is still open.
 */
type StepState = 'waiting' | 'running' | 'pending' | 'ok' | 'hit' | 'skipped' | 'error'

const STEP_ICONS: { readonly [K in KycStepCodeValue]: typeof Fingerprint } = {
  IDENT: Fingerprint,
  ZEFIX: Building2,
  SIGNATORY: PenLine,
  SECO: ShieldCheck,
  TRANSPARENCY_REGISTER: Landmark,
  REGULATED: BadgeCheck,
}

/** Colours per state — follow-ups stay amber, never alarm red. */
const STATE_TONES: Record<StepState, { marker: string; title: string; badge: string }> = {
  waiting: {
    marker: 'border-gray-200 bg-white text-gray-300',
    title: 'text-gray-400',
    badge: 'text-gray-400',
  },
  running: {
    marker: 'border-primary-300 bg-primary-50 text-primary-600',
    title: 'text-gray-900',
    badge: 'bg-primary-50 text-primary-700',
  },
  ok: {
    marker: 'border-emerald-200 bg-emerald-50 text-emerald-600',
    title: 'text-gray-900',
    badge: 'bg-emerald-50 text-emerald-700',
  },
  skipped: {
    marker: 'border-gray-200 bg-gray-50 text-gray-400',
    title: 'text-gray-500',
    badge: 'bg-gray-100 text-gray-500',
  },
  pending: {
    marker: 'border-amber-200 bg-amber-50 text-amber-600',
    title: 'text-gray-900',
    badge: 'bg-amber-50 text-amber-700',
  },
  hit: {
    marker: 'border-amber-200 bg-amber-50 text-amber-600',
    title: 'text-gray-900',
    badge: 'bg-amber-50 text-amber-700',
  },
  error: {
    marker: 'border-amber-200 bg-amber-50 text-amber-600',
    title: 'text-gray-900',
    badge: 'bg-amber-50 text-amber-700',
  },
}

const STATE_FALLBACK_LABELS: Record<StepState, string> = {
  waiting: 'Wartet',
  running: 'Läuft',
  pending: 'In Prüfung',
  ok: 'Geprüft',
  hit: 'In Prüfung',
  skipped: 'Nicht nötig',
  error: 'In Prüfung',
}

const STEP_TITLE_FALLBACKS: { readonly [K in KycStepCodeValue]: string } = {
  IDENT: 'Identität',
  ZEFIX: 'Handelsregister (Zefix)',
  SIGNATORY: 'Zeichnungsberechtigung',
  SECO: 'SECO-Sanktionsliste',
  TRANSPARENCY_REGISTER: 'Transparenzregister',
  REGULATED: 'Beaufsichtigte Gegenpartei',
}

interface Props {
  /** The run to display; `null` while it is being created. */
  run: KycCheckRun | null
  /**
   * Reveal the steps one by one. On for a live run, off for a past run that
   * is only being looked at (settings).
   */
  animate?: boolean
  /**
   * The run stopped reporting without ever reaching DONE/FAILED. Open steps
   * are then shown as «in Prüfung» instead of spinning forever.
   */
  stalled?: boolean
  /** Fired once every step has been revealed and the run is over. */
  onRevealComplete?: () => void
}

/** A finished step always carries `finished_at` — `PENDING` can be a result. */
const isSettled = (step: KycCheckStep): boolean => !!step.finished_at

export default function KycProgress({
  run,
  animate = true,
  stalled = false,
  onRevealComplete,
}: Props) {
  const { t } = useTranslation()
  const reducedMotion = usePrefersReducedMotion()
  const [revealed, setRevealed] = useState(0)

  const steps = useMemo(
    () => [...(run?.steps ?? [])].sort((a, b) => a.order - b.order),
    [run],
  )

  // The run is over — either reported by the backend (also FAILED) or given up
  // on by the caller after the poll limit.
  const runOver = (!!run && run.status !== KYC_RUN_STATUS.RUNNING) || stalled

  // How many leading steps the backend has actually finished. A run that is
  // over releases the rest so the list never stalls half-way.
  const settledCount = useMemo(() => {
    if (runOver) return steps.length
    const firstOpen = steps.findIndex((step) => !isSettled(step))
    return firstOpen === -1 ? steps.length : firstOpen
  }, [steps, runOver])

  // Restart the reveal for every new run
  useEffect(() => {
    setRevealed(0)
  }, [run?.id])

  // Reveal one finished step at a time — each stays readable for a moment
  useEffect(() => {
    if (!animate || revealed >= settledCount) return
    const timer = setTimeout(() => setRevealed((current) => current + 1), KYC_STEP_MIN_VISIBLE_MS)
    return () => clearTimeout(timer)
  }, [animate, revealed, settledCount])

  // A run without steps (e.g. one that failed right away) counts as revealed
  // straight away — otherwise the caller would wait for a signal that never
  // comes and its «Weiter» would stay disabled forever.
  const revealDone = !animate || revealed >= steps.length

  useEffect(() => {
    if (!animate || !revealDone || !runOver) return
    onRevealComplete?.()
  }, [animate, revealDone, runOver, onRevealComplete])

  const stateOf = (step: KycCheckStep, index: number): StepState => {
    // Nothing is running any more once we gave up — open steps go to the
    // manual check, so they read «In Prüfung» rather than spinning on.
    if (stalled && !isSettled(step)) return 'pending'
    if (animate) {
      if (index > revealed) return 'waiting'
      if (index === revealed) return 'running'
    } else if (!isSettled(step)) {
      return step.status === KYC_STEP_STATUS.RUNNING ? 'running' : 'waiting'
    }
    if (step.status === KYC_STEP_STATUS.RUNNING) return 'running'
    return step.status.toLowerCase() as StepState
  }

  const subtitleOf = (step: KycCheckStep, state: StepState): string =>
    t(`kyc.steps.${step.code}.${state}`, {
      defaultValue: state === 'waiting' || state === 'running' ? '' : step.summary || '',
      ...step.variables,
    })

  /** Small «live card» with the concrete finding of a step. */
  const cardOf = (step: KycCheckStep, state: StepState): React.ReactNode => {
    if (state !== 'ok') return null
    const cardClass =
      'mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600'

    if (step.code === KYC_STEP_CODE.ZEFIX && step.variables.UID) {
      return (
        <div className={cardClass} data-testid="kyc-progress-card-zefix">
          <p className="font-medium text-gray-900 break-words">{step.variables.COMPANY_NAME}</p>
          <p className="mt-0.5 break-all">
            {t('kyc.progress.cards.uid', 'UID')}: {step.variables.UID}
          </p>
        </div>
      )
    }

    if (step.code === KYC_STEP_CODE.SIGNATORY && step.variables.SIGNATURE_TYPE) {
      const raw = step.variables.SIGNATURE_TYPE
      const known = SIGNATURE_TYPES.find((type) => type === raw)
      const label = known ? t(SIGNATURE_TYPE_LABEL_KEYS[known], known) : raw
      return (
        <div className={cardClass} data-testid="kyc-progress-card-signatory">
          {t('kyc.progress.cards.signature', 'Eintrag im Handelsregister')}: {label}
        </div>
      )
    }

    if (step.code === KYC_STEP_CODE.SECO) {
      return (
        <div className={cardClass} data-testid="kyc-progress-card-seco">
          {t('kyc.progress.cards.seco', '0 Treffer auf der Sanktionsliste des Bundes (SECO)')}
        </div>
      )
    }

    return null
  }

  const stateIcon = (state: StepState): React.ReactNode => {
    switch (state) {
      case 'ok':
        return <Check size={16} aria-hidden="true" />
      case 'skipped':
        return <Minus size={16} aria-hidden="true" />
      case 'running':
        return (
          <Loader2
            size={16}
            aria-hidden="true"
            className={reducedMotion ? undefined : 'animate-spin'}
          />
        )
      case 'waiting':
        return <Circle size={10} aria-hidden="true" />
      default:
        return <Clock size={16} aria-hidden="true" />
    }
  }

  if (!run) {
    return (
      <div
        data-testid="kyc-progress-loading"
        className="flex items-center gap-2 text-sm text-gray-500"
      >
        <Loader2 size={16} className={reducedMotion ? undefined : 'animate-spin'} />
        {t('kyc.progress.starting', 'Einen Moment, wir prüfen Ihre Identität …')}
      </div>
    )
  }

  return (
    <ol
      data-testid="kyc-progress"
      className="space-y-0"
      aria-live="polite"
      aria-busy={!revealDone || !runOver}
      aria-label={t('kyc.progress.title', 'Prüfschritte')}
    >
      {steps.map((step, index) => {
        const state = stateOf(step, index)
        const tone = STATE_TONES[state]
        const StepIcon = STEP_ICONS[step.code] ?? ShieldCheck
        const subtitle = subtitleOf(step, state)
        const stateLabel = t(`kyc.progress.state.${state}`, STATE_FALLBACK_LABELS[state])

        return (
          <li
            key={step.code}
            data-testid={`kyc-progress-step-${step.code}`}
            data-state={state}
            className="relative flex gap-3 pb-4 last:pb-0"
          >
            {index < steps.length - 1 && (
              <span
                aria-hidden="true"
                className="absolute left-[15px] top-9 bottom-0 w-px bg-gray-200"
              />
            )}
            <span
              className={`relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border ${tone.marker} ${
                state === 'running' && !reducedMotion ? 'animate-pulse' : ''
              }`}
            >
              <StepIcon size={15} aria-hidden="true" />
            </span>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                <p className={`text-sm font-medium ${tone.title}`}>
                  {t(`kyc.steps.${step.code}.title`, STEP_TITLE_FALLBACKS[step.code] ?? step.code)}
                </p>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${tone.badge}`}
                >
                  {stateIcon(state)}
                  <span>{stateLabel}</span>
                </span>
              </div>
              {subtitle && <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>}
              {cardOf(step, state)}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
